import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { useOrderStore } from './useOrderStore'
import type { RegulationStatus } from '@/constants/regulationStatus'

export const useRegulationStore = defineStore('regulation', () => {
  const suiteStore = useSuiteStore()

  const isLoading = ref(false)
  const regulations = ref<any[]>([])
  const familyOptions = ref<any[]>([])

  const createRegulation = async (data: {
    quoteUuid: string
    salesEngineerUuid: string
    amount: number
    reason?: string
    family?: string
    contractCoef?: number
    durationCoef?: number
    paymentCoef?: number
    iavCoef?: number
    advUuid?: string
    validatedAt?: string
  }) => {
    isLoading.value = true
    try {
      const response = await suiteStore.suiteFetch('regulations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Erreur lors de la création de la régularisation:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateRegulationStatus = async (uuid: string, status: RegulationStatus, validatedAt?: string, advUuid?: string) => {
    try {
      const body: { uuid: string; status: RegulationStatus; validatedAt?: string; advUuid?: string } = { uuid, status }
      if (validatedAt) body.validatedAt = validatedAt
      if (advUuid && status === 'VALIDATED') body.advUuid = advUuid
      const response = await suiteStore.suiteFetch('regulations/status', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error)
      return null
    }
  }

  const fetchRegulation = async (uuid: string) => {
    try {
      const response = await suiteStore.suiteFetch(`regulations/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la régularisation:', error)
    }
    return null
  }

  const fetchRegulations = async (params?: { authorUuid?: string; search?: string; status?: string, family?: string[], orderTemplateCode?: string[] }) => {
    isLoading.value = true
    try {
      const search = new URLSearchParams()
      if (params?.authorUuid) search.set('authorUuid', params.authorUuid)
      if (params?.search) search.set('search', params.search)
      if (params?.status) search.set('status', params.status)
      if (params?.family) search.set('family', params.family.toString())
      if (params?.orderTemplateCode) search.set('orderTemplateCode', params.orderTemplateCode.toString())
      const qs = search.toString()
      const url = qs ? `regulations?${qs}` : 'regulations'
      const response = await suiteStore.suiteFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        regulations.value = await response.json()
        return regulations.value
      }
      regulations.value = []
      return []
    } catch (error) {
      console.error('Erreur lors de la récupération des régularisations:', error)
      regulations.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const fetchQuoteCommissions = async (quoteUuid: string) => {
    try {
      const response = await suiteStore.suiteFetch('regulations/quote-commissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quoteUuid }),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Erreur lors de la récupération des commissions:', error)
      return null
    }
  }

  const fetchFamilyOptions = async () => {
    try {
      const orderStore = useOrderStore()
      const data = await orderStore.ensureOrderModels()
      familyOptions.value = data.map((m: any) => {
        const name = m.template?.name || m.name || m.code
        return { id: m.code || m.id, value: m.code || name, label: name }
      })
      return familyOptions.value
    } catch (error) {
      console.error('Erreur lors de la récupération des familles:', error)
    }
    return []
  }

  return {
    isLoading,
    regulations,
    familyOptions,
    createRegulation,
    updateRegulationStatus,
    fetchRegulation,
    fetchRegulations,
    fetchQuoteCommissions,
    fetchFamilyOptions,
  }
})
